import React from "react";
import { AlertTriangle, RefreshCcw, ArrowLeft, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";

// 👇 Wrapper to use hook inside class
export function ErrorBoundaryWrapper({ children }) {
  const navigate = useNavigate();
  return <ErrorBoundary navigate={navigate}>{children}</ErrorBoundary>;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[300px] flex items-center justify-center p-6">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-6 text-center">
            {/* Icon */}
            <div className="mx-auto w-12 h-12 rounded-xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>

            {/* Title */}
            <div className="mt-4 text-[16px] font-semibold text-slate-900 dark:text-white">
              Something went wrong
            </div>

            {/* Message */}
            <div className="mt-1 text-[13px] text-slate-500 dark:text-slate-400">
              We couldn't load this section. Try again or go back.
            </div>

            {/* Dev error message */}
            {process.env.NODE_ENV !== "production" && this.state.error && (
              <div className="mt-3 text-[11px] text-red-500 text-left">
                <div className="text-center pb-2">
                  {this.state.error.message}
                </div>
                <details className="bg-red-50 dark:bg-red-500/10 rounded-lg p-2">
                  <summary className="cursor-pointer font-semibold mb-1">
                    Show Full Error
                  </summary>

                  <div className="max-h-40 overflow-auto whitespace-pre-wrap break-all pr-1">
                    {this.state.error?.message && (
                      <div>
                        <b>Message:</b> {this.state.error.message}
                      </div>
                    )}

                    {this.state.error?.response?.data && (
                      <div className="mt-1">
                        <b>Response:</b>
                        <pre className="whitespace-pre-wrap break-all">
                          {JSON.stringify(
                            this.state.error.response.data,
                            null,
                            2,
                          )}
                        </pre>
                      </div>
                    )}

                    {this.state.error?.stack && (
                      <div className="mt-1">
                        <b>Stack:</b>
                        <pre className="whitespace-pre-wrap break-all">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}

            {/* Actions */}
            <div className="mt-5 flex justify-center gap-2 flex-wrap">
              {/* Retry */}
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center gap-2 px-4 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-medium transition"
              >
                <RefreshCcw className="w-4 h-4" />
                Retry
              </button>

              {/* Home */}
              <button
                onClick={() => (window.location.href = "/dashboard")}
                className="inline-flex items-center gap-2 px-3 h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[12px] hover:bg-slate-50 dark:hover:bg-slate-700 transition"
              >
                <Home className="w-4 h-4" />
                Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
